/**
 * Home health demo — patients.
 *
 * The wound-VAC thread (M. Alvarez), the Medicare FFS pair referral, and the
 * census patients whose booked visits give the branch its shape. Addresses are
 * used by the tenant seed (real patients, geocoded); zips drive territory.
 */
import type { DemoPatient, FrequencyOrder, PatientConstraint, Payer } from "../state";
import { TRAIT_WOUND_VAC } from "./config";
import { splitDisplayName } from "./names";

export interface PatientFixture extends Omit<DemoPatient, "territory" | "serverId"> {
  age: number;
  sex: "F" | "M";
  address1: string;
  city: string;
  state: string;
  /** "lat,lng" — real geocodes borrowed from the Home Health Demo tenant so drive times work. */
  coordinates: string;
  caseType: string;
}

export interface ReferralFixture {
  id: string;
  patientRef: string;
  source: string;
  /** Hour:minute on demo day, branch zone. */
  receivedAt: string;
  caseType: string;
  orders: FrequencyOrder[];
  notes: string;
}

export const ALVAREZ: PatientFixture = {
  ref: "alvarez",
  displayName: "M. Alvarez",
  ...splitDisplayName("M. Alvarez", "F"),
  age: 78,
  sex: "F",
  payer: "MA",
  zip: "98115",
  address1: "1348 NE 62nd St",
  city: "Seattle",
  state: "WA",
  coordinates: "47.6740849,-122.3124379",
  caseType: "woundCareSnPt",
  constraints: [
    { kind: "language", value: "English" },
    { kind: "caregiver", value: "Caregiver present mornings (from referral; welcome call will confirm)" },
  ],
};

export const OKAFOR: PatientFixture = {
  ref: "okafor",
  displayName: "J. Okafor",
  ...splitDisplayName("J. Okafor", "M"),
  age: 81,
  sex: "M",
  payer: "FFS",
  zip: "98122",
  address1: "1107 E Howell St",
  city: "Seattle",
  state: "WA",
  coordinates: "47.603118,-122.289612",
  caseType: "chfSn",
  constraints: [{ kind: "language", value: "English" }],
};

/** Referral events the presenter fires (2.1 and the FFS pair in 2.4). */
export const REFERRAL_FIXTURES: ReferralFixture[] = [
  {
    id: "ref-alvarez",
    patientRef: "alvarez",
    source: "Riverbend Medical Center · hospital discharge, home today",
    receivedAt: "09:40",
    caseType: "woundCareSnPt",
    orders: [
      { discipline: "RN", pattern: "eval", requiredTraits: [TRAIT_WOUND_VAC] },
      { discipline: "PT", pattern: "eval" },
    ],
    notes: "SN eval & treat (wound VAC, sacral stage 3) · PT eval & treat · F2F on file · EHR ref #A-48213",
  },
  {
    id: "ref-okafor",
    patientRef: "okafor",
    source: "Swedish cardiology clinic · community referral",
    receivedAt: "10:15",
    caseType: "chfSn",
    // A clinic referral says which disciplines, not how many visits: the expected
    // plan is the branch's judgment, suggested from the case type.
    orders: [{ discipline: "RN", pattern: "eval" }],
    notes: "SN for CHF management · Medicare FFS · F2F on file",
  },
];

/**
 * `[display name, sex, age, explicit first name]`. The first name otherwise comes
 * from the initial, in the pool for the patient's sex.
 *
 * A row keeps its place after its patient leaves the seed (`RETIRED_PATIENT_REFS`),
 * so every other patient keeps its ref and its address.
 */
const CENSUS_NAMES: [string, "F" | "M", number, string?][] = [
  ["R. Whitfield", "M", 84],
  ["T. Nguyen", "M", 72],
  ["B. Carrillo", "F", 69],
  ["H. Patel", "M", 77, "Harold"],
  ["L. Jefferson", "F", 88],
  ["G. Moreau", "M", 65],
  ["S. Kowalski", "F", 79],
  ["D. Abernathy", "M", 83],
  ["P. Lindqvist", "F", 91],
  ["M. Okonkwo", "M", 70, "Mark"],
  ["A. Fitzgerald", "F", 74],
  ["C. Delgado", "M", 68],
  ["E. Baptiste", "F", 86, "Evelyn"],
  ["J. Kirkland", "M", 75],
  ["N. Sorensen", "F", 82],
  ["W. Haddad", "M", 79],
  ["K. Oyelaran", "F", 67, "Kathleen"],
  ["V. Castellano", "F", 90],
  ["F. Brennan", "F", 73],
  ["O. Ramaswamy", "M", 81],
  ["I. Novak", "F", 76],
  ["U. Mbeki", "M", 71],
  ["Y. Tanaka", "F", 85],
  ["Z. Petrov", "M", 78],
  ["Q. Ashworth", "F", 80],
  ["X. Villanueva", "M", 66],
  ["G. Lindstrom", "F", 89],
  ["R. Achebe", "M", 74],
  ["S. Marchetti", "F", 70],
  ["T. Halvorsen", "F", 87, "Theresa"],
];

const CENSUS_ADDRESSES: [string, string, string][] = [
  // [address1, zip, "lat,lng"] spread across territories; North is deliberately heavy.
  // One per patient, and distinct from each other on both street+zip and geocode:
  // two patients at one address read on the schedule as a visit with no drive.
  ["10047 32nd Ave NE", "98125", "47.70248,-122.2945029"],
  ["11510 30th Ave NE", "98125", "47.7128,-122.2963"],
  ["12515 35th Ave NE", "98125", "47.7213,-122.2905"],
  ["10315 25th Ave NE", "98125", "47.7043,-122.3018"],
  ["1026 NE 64th St", "98115", "47.6751401,-122.3159059"],
  ["6817 25th Ave NE", "98115", "47.6787,-122.3004"],
  ["7534 20th Ave NE", "98115", "47.6845,-122.3067"],
  ["10701 Greenwood Ave N", "98133", "47.701231,-122.3358293"],
  ["13020 Linden Ave N", "98133", "47.7247,-122.3480"],
  ["107 N 77th St", "98103", "47.6851,-122.3560"],
  ["4416 Phinney Ave N", "98103", "47.6607,-122.3541"],
  ["1423 NW 62nd St", "98107", "47.6561505,-122.3598107"],
  ["1019 26th Ave E", "98112", "47.618841,-122.2889363"],
  ["2626 E Madison St", "98112", "47.6255,-122.2966"],
  ["3010 E Denny Way", "98122", "47.6182,-122.2924"],
  ["1600 E Union St", "98122", "47.6130,-122.3105"],
  ["3709 S Dawson St", "98118", "47.5151373,-122.2551119"],
  ["4707 Rainier Ave S", "98118", "47.5588,-122.2853"],
  ["1005 S Snoqualmie St", "98108", "47.5615125,-122.3200928"],
  ["2916 S Alaska St", "98144", "47.5606,-122.2937"],
  ["107 Belmont Ave E", "98102", "47.6187963,-122.3242851"],
  ["1009 SW Elmgrove St", "98106", "47.5233027,-122.3472276"],
  ["10024 36th Ave SW", "98146", "47.513026,-122.3772732"],
  ["4441 44th Ave SW", "98116", "47.5590993,-122.3925291"],
  ["3416 SW Cambridge St", "98126", "47.5193822,-122.3763212"],
  ["4010 SW Graham St", "98136", "47.5226082,-122.3904621"],
  ["1000 Fairview Ave N", "98109", "47.6279899,-122.331314"],
  ["1620 1st Ave W", "98119", "47.63424759999999,-122.358019"],
  ["2001 8th Ave", "98121", "47.6158512,-122.3370442"],
  ["13010 3rd Ave NE", "98125", "47.7234752,-122.3254099"],
];

const CENSUS_CASE_TYPES = ["woundCareSnPt", "chfSn", "orthoPtOt", "strokeSnPtOtSlp", "hhaSupport"];
const CENSUS_PAYERS: Payer[] = ["FFS", "FFS", "MA", "FFS", "MA"];

export const CENSUS_PATIENTS: PatientFixture[] = CENSUS_NAMES.map(([displayName, sex, age, firstName], i) => ({
  ref: `census-${String(i + 1).padStart(2, "0")}`,
  displayName,
  ...splitDisplayName(displayName, sex, i + 2),
  ...(firstName ? { firstName } : {}),
  sex,
  age,
  payer: CENSUS_PAYERS[i % CENSUS_PAYERS.length],
  zip: CENSUS_ADDRESSES[i][1],
  address1: CENSUS_ADDRESSES[i][0],
  city: "Seattle",
  state: "WA",
  coordinates: CENSUS_ADDRESSES[i][2],
  caseType: CENSUS_CASE_TYPES[i % CENSUS_CASE_TYPES.length],
  constraints: [],
}));


/** Dana's Thursday-and-Friday patient (3.2, 3.3). North. */
export const OSEI: PatientFixture = {
  ref: "osei",
  displayName: "K. Osei",
  firstName: "Kenneth",
  lastName: "Osei",
  age: 80,
  sex: "M",
  payer: "FFS",
  zip: "98117",
  address1: "100 NW 84th St",
  city: "Seattle",
  state: "WA",
  coordinates: "47.6900298,-122.3581892",
  caseType: "chfSn",
  constraints: [],
};

/** Not home at 2:00 today; slides into Thursday's gap (3.3). North. */
export const DUARTE: PatientFixture = {
  ref: "duarte",
  displayName: "E. Duarte",
  firstName: "Evelyn",
  lastName: "Duarte",
  age: 83,
  sex: "F",
  payer: "FFS",
  zip: "98133",
  address1: "11019 Palatine Ave N",
  city: "Seattle",
  state: "WA",
  coordinates: "47.70938959999999,-122.3571196",
  caseType: "chfSn",
  constraints: [],
};

/** Marcus's wound-VAC visit at 2:30 today; the one that needs coverage in 4.1. East. */
export const FERREIRA: PatientFixture = {
  ref: "ferreira",
  displayName: "R. Ferreira",
  firstName: "Rita",
  lastName: "Ferreira",
  age: 76,
  sex: "F",
  payer: "FFS",
  zip: "98112",
  address1: "1214 42nd Ave E",
  city: "Seattle",
  state: "WA",
  coordinates: "47.6302927,-122.2789253",
  caseType: "woundCareSnPt",
  constraints: [],
};

export const NAMED_PATIENTS: PatientFixture[] = [OSEI, DUARTE, FERREIRA];

/**
 * Patients retired from the seed. D. Abernathy (census-08) was a second Needs a
 * time row on Pending admissions that no beat named (Pete, 2026-10-08); T.
 * Nguyen, B. Carrillo, G. Moreau, P. Lindqvist and E. Duarte left with the beats
 * the 30-minute script cut (2026-10-12). Her slot
 * stays in `PATIENT_SLOTS`, so the generator gives every other patient the same
 * index, and her address stays spent, so the generated census draws the same
 * addresses.
 */
export const RETIRED_PATIENT_REFS: ReadonlySet<string> = new Set(["census-08", "census-03", "census-02", "census-06", "census-09", "duarte"]);

/** Every patient slot in seed order, retired ones included. */
export const PATIENT_SLOTS: PatientFixture[] = [ALVAREZ, OKAFOR, ...NAMED_PATIENTS, ...CENSUS_PATIENTS];

export const ALL_PATIENT_FIXTURES: PatientFixture[] = PATIENT_SLOTS.filter((p) => !RETIRED_PATIENT_REFS.has(p.ref));

/** Okafor's welcome call (FFS pair, 2.4.2). */
export const OKAFOR_WELCOME_CALL: PatientConstraint[] = [
  { kind: "availabilityDays", value: "Mon-Sat" },
  { kind: "availabilityWindow", value: "10:00-16:00" },
  { kind: "smsConsent", value: "yes" },
];

/** The welcome call for the patient admitted two days ago said mornings (2.1). */
export const ADMITTED_MONDAY_WELCOME_CALL: PatientConstraint[] = [
  { kind: "availabilityWindow", value: "08:00-12:00" },
  { kind: "smsConsent", value: "yes" },
];

/** The clinician who added the note at the door; the availability report prints the name. */
export const ADMITTED_MONDAY_NOTE_AUTHOR = "Dana Reyes";

/**
 * What the welcome call captured for the patient admitted two days ago, plus what
 * Dana learned at yesterday's visit (2.5, 3.2): the daughter is there Tuesdays, so
 * nursing does not come on a Tuesday. It is a **hard** day rule — the check flags
 * the nursing visits that land on a Tuesday, the alerts panel carries the nearest
 * one, and 3.2's drag of that visit to Wednesday clears it.
 *
 * Every other day of the week is listed: the note is about Tuesdays, and nothing
 * here should assert a weekend rule the welcome call never captured.
 */
const DAY_ABBR = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_PLURAL = ["Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays", "Sundays"];

/**
 * What Dana learned at the door yesterday: the daughter is there on one weekday,
 * so he is not available that day. It is a weekday none of his visits falls on
 * (`notWeekday`, ISO; `doorLearnedWeekday` in `seed/schedule.ts` picks it from the
 * seed), so nothing seeded conflicts with it and his empty day is the one the
 * Reserve amber drop lands on (SEED-SCOPE, Pete 2026-10-12).
 */
export function admittedMondayConstraints(noteAddedAtIso: string, notWeekday: number): PatientConstraint[] {
  return [
    ...ADMITTED_MONDAY_WELCOME_CALL,
    { kind: "availabilityDays", value: DAY_ABBR.filter((_, i) => i + 1 !== notWeekday).join(","), addedBy: ADMITTED_MONDAY_NOTE_AUTHOR, addedAtIso: noteAddedAtIso },
    { kind: "caregiver", value: `Daughter present ${DAY_PLURAL[notWeekday - 1]}`, addedBy: ADMITTED_MONDAY_NOTE_AUTHOR, addedAtIso: noteAddedAtIso },
  ];
}

